/**
 * Section B7.1 Page - B.7.1.日常與基準計算
 * Calculation of BAU emissions/removals and baseline emissions/removals
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import RichTextEditor from '../components/RichTextEditor';
import sectionB71Service, {
  SectionB71Detail, FormulaEntry, FormulaParameter
} from '../services/sectionB71Service';
import { getSystemCodes, SystemCode } from '../services/systemCodeService';
import '../styles/DataTable.css';
import LatexHelper from '../components/LatexHelper';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import { parseLatexSymbols, normalizeSubscripts } from '../utils/latexEvaluator';
import formulaConfigService, { FormulaConfigItem } from '../services/formulaConfigService';

// ── 常數 ──────────────────────────────────────────────────────────
type WizardStage = 'equations' | 'methodological_choices' | 'conservative_estimates' | 'sample_plans';
type FormulaType = 'bau' | 'baseline';

const EMPTY_ENTRY = (): FormulaEntry => ({
  id: crypto.randomUUID(),
  methodology: '',
  equation: { latex: '' },
  parameters: [],
  calculation_result: { value: null, unit: '' },
  is_sampling: false,
  sample_plans: '',
});

// ── 數值格式化（千分號）────────────────────────────────────────────
function fmtNum(value: number | null | undefined, decimals = 5): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

// ── 單位 LaTeX 輸入元件 ──────────────────────────────────────────
const COMMON_UNITS = [
  { label: 'tCO₂e',  latex: '\\text{tCO}_2\\text{e}' },
  { label: 'ktCO₂e', latex: '\\text{ktCO}_2\\text{e}' },
  { label: 'MtCO₂e', latex: '\\text{MtCO}_2\\text{e}' },
  { label: 'MWh',    latex: '\\text{MWh}' },
  { label: 'GWh',    latex: '\\text{GWh}' },
  { label: 'kWh',    latex: '\\text{kWh}' },
  { label: 'GJ',     latex: '\\text{GJ}' },
  { label: 'MJ',     latex: '\\text{MJ}' },
  { label: 'kg',     latex: '\\text{kg}' },
  { label: 't',      latex: '\\text{t}' },
  { label: 'm³',     latex: '\\text{m}^3' },
  { label: 'Nm³',    latex: '\\text{Nm}^3' },
  { label: 'L',      latex: '\\text{L}' },
  { label: '%',      latex: '\\%' },
  { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' },
  { label: 'tCO₂e/GJ',  latex: '\\text{tCO}_2\\text{e/GJ}' },
];

interface UnitInputProps {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  compact?: boolean;   // true = 單行緊湊模式（用於參數表格）
}

const UnitInput: React.FC<UnitInputProps> = ({ value, onChange, disabled = false, compact = false }) => {
  const [showPicker, setShowPicker] = useState(false);

  const preview = () => {
    if (!value.trim()) return null;
    try { return <InlineMath math={value} />; } catch { return <span style={{ color: '#c33', fontSize: 11 }}>!</span>; }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          placeholder={compact ? 'unit LaTeX' : '單位 LaTeX，如 \\text{tCO}_2\\text{e}'}
          style={{
            flex: 1, padding: compact ? '4px 6px' : '8px 10px',
            border: '1px solid #d0d0d0', borderRadius: 6,
            fontFamily: 'monospace', fontSize: compact ? 12 : 13,
          }}
        />
        {/* 預覽 */}
        {value.trim() && (
          <div style={{
            minWidth: compact ? 40 : 60, padding: '2px 6px',
            background: '#f8faff', border: '1px solid #d0e4ff', borderRadius: 5,
            fontSize: compact ? 12 : 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {preview()}
          </div>
        )}
        {/* 常用單位按鈕 */}
        {!disabled && (
          <button type="button" onClick={() => setShowPicker(p => !p)}
            title="常用單位"
            style={{
              padding: '4px 7px', fontSize: 13, borderRadius: 5,
              border: '1px solid #d0d0d0', background: showPicker ? '#4A90E2' : '#fafafa',
              color: showPicker ? '#fff' : '#555', cursor: 'pointer', lineHeight: 1,
            }}>≡</button>
        )}
      </div>

      {/* 常用單位下拉 */}
      {showPicker && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, zIndex: 200, marginTop: 4,
          background: '#fff', border: '1px solid #d0d0d0', borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '8px 10px', width: 320,
        }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>常用單位（點擊插入）</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {COMMON_UNITS.map((u, i) => (
              <button key={i} type="button"
                onClick={() => { onChange(u.latex); setShowPicker(false); }}
                style={{
                  padding: '4px 10px', fontSize: 12, borderRadius: 5,
                  border: '1px solid #e0e0e0', background: '#fafafa',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                }}>
                <span style={{ color: '#888', fontSize: 11 }}>{u.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── LaTeX 公式計算 ────────────────────────────────────────────────
/**
 * 將 LaTeX 公式代入參數數值後求解
 * 支援：+, -, *, /, \times, \div, \cdot, \frac{}{}, \sqrt{}, ^{} (次方), \sum (移除求和符號保留內容)
 * 回傳 null 表示無法計算（含未知符號或語法錯誤）
 */
function evaluateLatex(latex: string, params: FormulaParameter[]): number | null {
  try {
    // 取等號右側
    let expr = latex.includes('=')
      ? latex.split(/(?<![<>!])=(?!=)/).slice(1).join('=')
      : latex;

    // 正規化頂層單字元下標（OX_y → OX_{y}）
    { let r = '', d = 0, i = 0; while (i < expr.length) { const c = expr[i]; if (c === '\\') { r += c + (expr[i+1]??''); i+=2; } else if (c === '{') { d++; r+=c; i++; } else if (c === '}') { d--; r+=c; i++; } else if (d===0 && c==='_' && i+1<expr.length && /[A-Za-z0-9]/.test(expr[i+1])) { r+='_{'+expr[i+1]+'}'; i+=2; } else { r+=c; i++; } } expr=r; }

    // 依符號長度由長到短替換，避免短符號污染長符號
    const filled = [...params]
      .filter(p => p.annual_value !== null && p.symbol.trim())
      .sort((a, b) => b.symbol.length - a.symbol.length);

    for (const p of filled) {
      const sym = normalizeSubscripts(p.symbol);
      const escaped = sym.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      expr = expr.replace(new RegExp(escaped, 'g'), `(${p.annual_value})`);
    }

    // LaTeX → JS 數學式轉換
    expr = expr
      // \frac{a}{b} → (a)/(b)
      .replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '($1)/($2)')
      // \sqrt{x} → Math.sqrt(x)
      .replace(/\\sqrt\{([^}]*)\}/g, 'Math.sqrt($1)')
      // 移除 \sum / \prod / \int 等結構符號（保留後面的 {content}）
      .replace(/\\(?:sum|prod|int)(?:_\{[^}]*\})?(?:\^\{[^}]*\})?/g, '')
      // 運算符
      .replace(/\\times/g, '*')
      .replace(/\\div/g, '/')
      .replace(/\\cdot/g, '*')
      // 括號
      .replace(/\\left\s*\(/g, '(').replace(/\\right\s*\)/g, ')')
      .replace(/\\left\s*\[/g, '(').replace(/\\right\s*\]/g, ')')
      .replace(/\[/g, '(').replace(/\]/g, ')')
      .replace(/\\left\s*\\{/g, '(').replace(/\\right\s*\\}/g, ')')
      // 次方 ^{n} → **(n)
      .replace(/\^\{([^}]*)\}/g, '**($1)')
      .replace(/\^([0-9]+)/g, '**$1')
      // 移除剩餘 LaTeX 指令
      .replace(/\\[a-zA-Z]+/g, '')
      // { } → ( )
      .replace(/\{/g, '(').replace(/\}/g, ')')
      // 清理底線（sub-script 殘留）
      .replace(/_/g, '');

    // 只允許安全字元
    if (/[^0-9+\-*/().%\s,MathsqrtpowPIE]/.test(expr.replace(/Math\.[a-z]+/g, ''))) return null;

    // eslint-disable-next-line no-new-func
    const result = new Function(`return (${expr})`)();
    if (typeof result !== 'number' || !isFinite(result)) return null;
    return Math.round(result * 100000) / 100000;  // 最多 5 位小數
  } catch {
    return null;
  }
}

// ── 公式精靈元件 ──────────────────────────────────────────────────
interface FormulaWizardProps {
  isZh: boolean;
  canEdit: boolean;
  type: FormulaType;
  projectId: number;
  editEntry?: FormulaEntry;
  onSaved: (data: SectionB71Detail) => void;
  onCancel: () => void;
}

const FormulaWizard: React.FC<FormulaWizardProps> = ({
  isZh, canEdit, type, projectId, editEntry, onSaved, onCancel
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [stage, setStage] = useState<WizardStage>('equations');
  const [entry, setEntry] = useState<FormulaEntry>(editEntry ? { ...editEntry } : EMPTY_ENTRY());
  const [saving, setSaving] = useState(false);
  const [latexError, setLatexError] = useState('');
  const [paraTypeCodes, setParaTypeCodes] = useState<SystemCode[]>([]);
  const [formulaConfigs, setFormulaConfigs] = useState<FormulaConfigItem[]>([]);
  const [formulaId, setFormulaId] = useState<number | ''>('');

  // Load para_type codes for baseline
  useEffect(() => {
    if (type === 'baseline') {
      getSystemCodes({ code_etype: 'equation parameters type', is_active: true })
        .then(codes => setParaTypeCodes(codes))
        .catch(() => setParaTypeCodes([]));
    }
  }, [type]);
  useEffect(() => { formulaConfigService.listActive().then(setFormulaConfigs).catch(() => {}); }, []);

  const stageLabels: Record<WizardStage, string> = {
    equations: t('sectionB71.stageEquations'),
    methodological_choices: t('sectionB71.stageMethodChoice'),
    conservative_estimates: t('sectionB71.stageConservative'),
    sample_plans: t('sectionB71.stageSample'),
  };
  const stageOrder: WizardStage[] = ['equations', 'methodological_choices', 'conservative_estimates', 'sample_plans'];
  const currentIdx = stageOrder.indexOf(stage);

  // 新增參數列
  const addParam = () => {
    setEntry(prev => ({
      ...prev,
      parameters: [...prev.parameters, { symbol: '', para_type: null, scenarios: '', unit: '', annual_value: null }]
    }));
  };

  const updateParam = (idx: number, field: keyof FormulaParameter, value: string | number | null) => {
    setEntry(prev => {
      const params = [...prev.parameters];
      params[idx] = { ...params[idx], [field]: value };
      return { ...prev, parameters: params };
    });
  };

  const removeParam = (idx: number) => {
    setEntry(prev => ({ ...prev, parameters: prev.parameters.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    // Validate: if is_sampling=true, sample_plans must not be empty
    if (entry.is_sampling) {
      const text = entry.sample_plans.replace(/<[^>]+>/g, '').trim();
      if (!text) {
        toast.warning(t('sectionB71.sampleRequired'));
        return;
      }
    }
    try {
      setSaving(true);
      let result: SectionB71Detail;
      if (editEntry) {
        if (type === 'bau') {
          result = await sectionB71Service.updateBauFormula(projectId, entry.id, entry);
        } else {
          result = await sectionB71Service.updateBaselineFormula(projectId, entry.id, entry);
        }
      } else {
        if (type === 'bau') {
          result = await sectionB71Service.addBauFormula(projectId, entry);
        } else {
          result = await sectionB71Service.addBaselineFormula(projectId, entry);
        }
      }
      onSaved(result);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('sectionB71.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const btnStyle = (primary: boolean, disabled = false) => ({
    padding: '8px 20px',
    borderRadius: 6,
    border: 'none',
    background: disabled ? '#ccc' : primary ? '#4A90E2' : '#f0f0f0',
    color: disabled ? '#888' : primary ? '#fff' : '#333',
    cursor: disabled ? 'not-allowed' as const : 'pointer' as const,
    fontWeight: 500,
    fontSize: 14,
  });

  return (
    <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 24 }}>
      {/* 步驟列 */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid #e0e0e0', paddingBottom: 12 }}>
        {stageOrder.map((s, i) => (
          <div key={s} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%', margin: '0 auto 4px',
              background: i < currentIdx ? '#52c41a' : i === currentIdx ? '#4A90E2' : '#e0e0e0',
              color: i <= currentIdx ? '#fff' : '#999',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 600, fontSize: 13,
            }}>{i < currentIdx ? '✓' : i + 1}</div>
            <div style={{ fontSize: 12, color: i === currentIdx ? '#4A90E2' : '#999', fontWeight: i === currentIdx ? 600 : 400 }}>
              {stageLabels[s]}
            </div>
          </div>
        ))}
      </div>

      {/* Stage 1: 公式 */}
      {stage === 'equations' && (
        <div>
          {/* 適用方法論 */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
              {t('sectionB71.methodologyLabel')}
            </label>
            <input
              value={entry.methodology}
              onChange={e => setEntry(prev => ({ ...prev, methodology: e.target.value }))}
              disabled={!canEdit}
              placeholder={t('sectionB71.methodologyPlaceholder')}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d0d0d0',
                borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
              }}
            />
          </div>

          {/* 從公式庫選取 */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>{isZh ? '從公式庫選取（選填）' : 'Select from Formula Library (optional)'}</label>
            <select value={formulaId} onChange={e => {
              const id = e.target.value ? Number(e.target.value) : '';
              setFormulaId(id);
              if (!id) return;
              const fc = formulaConfigs.find(f => f.id === id);
              if (fc?.formula_latex) setEntry(prev => ({ ...prev, equation: { latex: fc.formula_latex! } }));
            }} style={{ width: '100%', padding: '8px 12px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14 }}>
              <option value="">-- {isZh ? '手動輸入' : 'Enter manually'} --</option>
              {formulaConfigs.map(f => <option key={f.id} value={f.id}>{f.formula_code}：{isZh ? f.name_zh : f.name_en}</option>)}
            </select>
          </div>

          {/* LaTeX 公式 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontWeight: 600 }}>{t('sectionB71.latexEquation')}</label>
            <LatexHelper isZh={isZh} onInsert={snippet =>
              setEntry(prev => ({ ...prev, equation: { latex: prev.equation.latex + snippet } }))
            } />
          </div>
          <textarea
            value={entry.equation.latex}
            onChange={e => {
              setEntry(prev => ({ ...prev, equation: { latex: e.target.value } }));
              setLatexError('');
            }}
            placeholder="e.g. BE_{y}^{BAU} = \sum_{i} (AD_{i,y}^{BAU} \times EF_{i,y}^{BAU})"
            style={{
              width: '100%', minHeight: 80, padding: '10px 12px', fontFamily: 'monospace',
              border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14, resize: 'vertical', boxSizing: 'border-box',
            }}
            disabled={!canEdit}
          />
          {/* 即時預覽 */}
          {entry.equation.latex.trim() && (
            <div style={{ marginTop: 12, padding: '12px 16px', background: '#f8f8f8', borderRadius: 6, border: '1px solid #e8e8e8' }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>{t('sectionB71.preview')}</div>
              {(() => {
                try {
                  return <BlockMath math={entry.equation.latex} />;
                } catch {
                  return <span style={{ color: '#c33', fontSize: 13 }}>{t('sectionB71.latexError')}</span>;
                }
              })()}
            </div>
          )}
          {latexError && <div style={{ color: '#c33', fontSize: 13, marginTop: 4 }}>{latexError}</div>}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <button style={btnStyle(false)} onClick={onCancel}>{t('sectionB71.cancel')}</button>
            <button
              style={btnStyle(true, !entry.equation.latex.trim())}
              disabled={!entry.equation.latex.trim()}
              onClick={() => {
                const parsed = parseLatexSymbols(entry.equation.latex);
                const cfgParams = formulaId ? (formulaConfigs.find(f => f.id === formulaId)?.parameters ?? []) : [];
                if (parsed.length > 0) {
                  setEntry(prev => {
                    const existing = new Set(prev.parameters.map(p => p.symbol));
                    const newParams: FormulaParameter[] = parsed
                      .filter(sym => !existing.has(sym))
                      .map(sym => {
                        const cp = cfgParams.find(p => p.symbol === sym);
                        return { symbol: sym, para_type: null, scenarios: cp ? (isZh ? [cp.name_zh, cp.description_zh] : [cp.name_en || cp.name_zh, cp.description_en || cp.description_zh]).filter(Boolean).join(' ') : '', unit: cp?.unit ?? '', annual_value: null };
                      });
                    return { ...prev, parameters: [...prev.parameters, ...newParams] };
                  });
                }
                setStage('methodological_choices');
              }}
            >
              {t('sectionB71.nextMethodChoice')}
            </button>
          </div>
        </div>
      )}

      {/* Stage 2: 方法論說明 */}
      {stage === 'methodological_choices' && (
        <div>
          {/* 公式回顯 */}
          <div style={{ padding: '10px 16px', background: '#f0f7ff', borderRadius: 6, border: '1px solid #bde0ff', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#4A90E2', marginBottom: 4, fontWeight: 600 }}>
              {t('sectionB71.equationPrefix')}
            </div>
            {(() => { try { return <BlockMath math={entry.equation.latex} />; } catch { return null; } })()}
          </div>

          {/* 參數表格 */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontWeight: 600 }}>{t('sectionB71.parametersLabel')}</label>
              {canEdit && (
                <button style={btnStyle(false)} onClick={addParam}>
                  + {t('sectionB71.addParameter')}
                </button>
              )}
            </div>
            {entry.parameters.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead className="table-header-dark-green">
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB71.symbol')}
                    </th>
                    {type === 'baseline' && (
                      <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                        {t('sectionB71.paraType')}
                      </th>
                    )}
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {type === 'bau' ? t('sectionB71.scenariosHtml') : t('sectionB71.scenariosText')}
                    </th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB71.unitLabel')}
                    </th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {entry.parameters.map((p, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                        <input value={p.symbol} onChange={e => updateParam(idx, 'symbol', e.target.value)}
                          style={{ width: '100%', border: 'none', outline: 'none', fontFamily: 'monospace', fontSize: 13 }}
                          disabled={!canEdit} placeholder="AD_{i,y}" />
                      </td>
                      {type === 'baseline' && (
                        <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', minWidth: 140 }}>
                          <select
                            value={p.para_type ?? ''}
                            onChange={e => updateParam(idx, 'para_type', e.target.value === '' ? null : Number(e.target.value))}
                            disabled={!canEdit}
                            style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, background: 'transparent' }}
                          >
                            <option value="">{t('sectionB71.allTypes')}</option>
                            {paraTypeCodes.map(c => (
                              <option key={c.id} value={c.id}>{c.code_cname}</option>
                            ))}
                          </select>
                        </td>
                      )}
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', minWidth: 200 }}>
                        {type === 'bau' ? (
                          <RichTextEditor
                            value={p.scenarios}
                            onChange={html => updateParam(idx, 'scenarios', html)}
                            placeholder={t('sectionB71.scenariosHtml')}
                            disabled={!canEdit}
                            minHeight="72px"
                          />
                        ) : (
                          <input
                            value={p.scenarios}
                            onChange={e => updateParam(idx, 'scenarios', e.target.value)}
                            style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13 }}
                            disabled={!canEdit}
                            placeholder={t('sectionB71.scenariosText')}
                          />
                        )}
                      </td>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                        <UnitInput compact value={p.unit} onChange={v => updateParam(idx, 'unit', v)} disabled={!canEdit} />
                      </td>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0', textAlign: 'center' }}>
                        {canEdit && (
                          <button onClick={() => removeParam(idx)}
                            style={{ background: 'none', border: 'none', color: '#e53935', cursor: 'pointer', fontSize: 16 }}>✕</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '12px', color: '#aaa', textAlign: 'center', border: '1px dashed #e0e0e0', borderRadius: 6 }}>
                {t('sectionB71.noParams')}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <button style={btnStyle(false)} onClick={() => setStage('equations')}>← {t('sectionB71.backToEquations')}</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnStyle(false)} onClick={onCancel}>{t('sectionB71.cancel')}</button>
              <button style={btnStyle(true)} onClick={() => setStage('conservative_estimates')}>
                {t('sectionB71.nextConservative')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage 3: 保守估算 */}
      {stage === 'conservative_estimates' && (
        <div>
          {/* 公式回顯 */}
          <div style={{ padding: '10px 16px', background: '#f0f7ff', borderRadius: 6, border: '1px solid #bde0ff', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#4A90E2', marginBottom: 4, fontWeight: 600 }}>
              {t('sectionB71.equationPrefix')}
            </div>
            {(() => { try { return <BlockMath math={entry.equation.latex} />; } catch { return null; } })()}
          </div>

          {/* 參數數值輸入 */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
              {t('sectionB71.conservativeValue')}
            </label>
            {entry.parameters.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead className="table-header-dark-green">
                  <tr style={{ background: '#f5f5f5' }}>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB71.symbol')}
                    </th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB71.conservativeValue')}
                    </th>
                    <th style={{ padding: '6px 10px', border: '1px solid #e0e0e0', textAlign: 'left', fontSize: 12 }}>
                      {t('sectionB71.unitLabel')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entry.parameters.map((p, idx) => (
                    <tr key={idx}>
                      <td style={{ padding: '4px 10px', border: '1px solid #e0e0e0', fontFamily: 'monospace' }}>
                        {p.symbol || '—'}
                      </td>
                      <td style={{ padding: '4px 6px', border: '1px solid #e0e0e0' }}>
                        <input
                          type="number"
                          step="0.01"
                          value={p.annual_value ?? ''}
                          onChange={e => updateParam(idx, 'annual_value', e.target.value === '' ? null : Number(e.target.value))}
                          style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13 }}
                          disabled={!canEdit}
                        />
                      </td>
                      <td style={{ padding: '4px 10px', border: '1px solid #e0e0e0' }}>
                        {p.unit ? (
                          (() => { try { return <InlineMath math={p.unit} />; } catch { return <span>{p.unit}</span>; } })()
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '12px', color: '#aaa', textAlign: 'center', border: '1px dashed #e0e0e0', borderRadius: 6 }}>
                {t('sectionB71.noParams')}
              </div>
            )}
          </div>

          {/* 計算區 */}
          <div style={{ marginTop: 16, padding: '14px 16px', background: '#f8faff', borderRadius: 8, border: '1px solid #d0e4ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {/* 結果單位輸入 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{t('sectionB71.resultUnit')}</label>
                <div style={{ width: 220 }}>
                  <UnitInput
                    value={entry.calculation_result.unit}
                    onChange={v => setEntry(prev => ({ ...prev, calculation_result: { ...prev.calculation_result, unit: v } }))}
                    disabled={!canEdit}
                    compact
                  />
                </div>
              </div>
              {/* 計算按鈕 */}
              {canEdit && (
                <button
                  onClick={() => {
                    const val = evaluateLatex(entry.equation.latex, entry.parameters);
                    if (val !== null) {
                      setEntry(prev => ({ ...prev, calculation_result: { ...prev.calculation_result, value: val } }));
                    } else {
                      toast.warning(t('sectionB71.calcAutoError'));
                    }
                  }}
                  style={{
                    padding: '7px 20px', background: '#52c41a', color: '#fff',
                    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13
                  }}
                >
                  ▶ {t('sectionB71.calculating')}
                </button>
              )}
              {/* 計算結果顯示 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                <span style={{ fontSize: 13, color: '#888' }}>{t('sectionB71.calcResult')}</span>
                <span style={{
                  fontSize: 20, fontWeight: 700,
                  color: entry.calculation_result.value !== null ? '#1a1a1a' : '#ccc',
                  minWidth: 80, textAlign: 'right',
                }}>
                  {fmtNum(entry.calculation_result.value)}
                </span>
                {entry.calculation_result.unit && (
                  <span style={{ fontSize: 13, color: '#555' }}>
                    {(() => { try { return <InlineMath math={entry.calculation_result.unit} />; } catch { return entry.calculation_result.unit; } })()}
                  </span>
                )}
              </div>
            </div>
            {entry.calculation_result.value !== null && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#52c41a' }}>
                ✓ {t('sectionB71.calcConfirm')}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
            <button style={btnStyle(false)} onClick={() => setStage('methodological_choices')}>← {t('sectionB71.backToMethodChoice')}</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnStyle(false)} onClick={onCancel}>{t('sectionB71.cancel')}</button>
              <button style={btnStyle(true)} onClick={() => setStage('sample_plans')}>
                {t('sectionB71.nextSample')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stage 4: 樣本計畫 */}
      {stage === 'sample_plans' && (
        <div>
          {/* is_sampling toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
            <label style={{ fontWeight: 600 }}>
              {t('sectionB71.isSampling')}
            </label>
            <div
              onClick={() => canEdit && setEntry(prev => ({ ...prev, is_sampling: !prev.is_sampling }))}
              style={{
                width: 52, height: 28, borderRadius: 14, position: 'relative',
                background: entry.is_sampling ? '#4A90E2' : '#ccc',
                cursor: canEdit ? 'pointer' : 'not-allowed',
                transition: 'background 0.25s',
                flexShrink: 0,
              }}
            >
              <div style={{
                position: 'absolute', top: 3, left: entry.is_sampling ? 27 : 3,
                width: 22, height: 22, borderRadius: '50%', background: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                transition: 'left 0.25s',
              }} />
            </div>
            <span style={{ fontWeight: 500, color: entry.is_sampling ? '#4A90E2' : '#999' }}>
              {entry.is_sampling ? t('sectionB71.yes') : t('sectionB71.no')}
            </span>
          </div>

          {entry.is_sampling && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                {t('sectionB71.sampleDescLabel')}
              </label>
              <RichTextEditor
                value={entry.sample_plans}
                onChange={html => setEntry(prev => ({ ...prev, sample_plans: html }))}
                placeholder={t('sectionB71.samplePlaceholder')}
                disabled={!canEdit}
              />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
            <button style={btnStyle(false)} onClick={() => setStage('conservative_estimates')}>← {t('sectionB71.backToConservative')}</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={btnStyle(false)} onClick={onCancel}>{t('sectionB71.cancel')}</button>
              {canEdit && (
                <button style={btnStyle(true, saving)} disabled={saving} onClick={handleSave}>
                  {saving ? t('sectionB71.saving') : t('sectionB71.save')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── 公式 DataTable ────────────────────────────────────────────────
interface FormulaTableProps {
  isZh: boolean;
  canEdit: boolean;
  type: FormulaType;
  entries: FormulaEntry[];
  onEdit: (entry: FormulaEntry) => void;
  onDelete: (entryId: string) => void;
  onAdd: () => void;
}

const FormulaTable: React.FC<FormulaTableProps> = ({ isZh, canEdit, type, entries, onEdit, onDelete, onAdd }) => {
  const { t } = useTranslation();
  return (
  <div>
    {canEdit && (
      <div style={{ marginBottom: 12 }}>
        <button onClick={onAdd} style={{
          padding: '8px 18px', background: '#4A90E2', color: '#fff', border: 'none',
          borderRadius: 6, cursor: 'pointer', fontWeight: 500
        }}>
          + {t('sectionB71.addFormula')}
        </button>
      </div>
    )}
    {entries.length === 0 ? (
      <div style={{ padding: '32px', textAlign: 'center', color: '#aaa', border: '1px dashed #e0e0e0', borderRadius: 8 }}>
        {t('sectionB71.noData')}
      </div>
    ) : (
      <div className="table-container">
        <table className="data-table" style={{ fontSize: 13 }}>
          <thead className="table-header-dark-green">
            <tr>
              <th style={{ width: 40 }}>#</th>
              <th>{t('sectionB71.colMethodology')}</th>
              <th>{t('sectionB71.colEquation')}</th>
              <th>{t('sectionB71.colParameters')}</th>
              <th>{t('sectionB71.colResult')}</th>
              <th>{t('sectionB71.colSample')}</th>
              {canEdit && <th style={{ width: 100 }}>{t('sectionB71.colActions')}</th>}
            </tr>
          </thead>
          <tbody>
            {entries.map((e, idx) => (
              <tr key={e.id}>
                <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                <td>
                  <div style={{ fontWeight: 500 }}>{e.methodology || '-'}</div>
                </td>
                <td>
                  {e.equation.latex ? (
                    <div style={{ maxWidth: 280, overflow: 'auto' }}>
                      {(() => { try { return <BlockMath math={e.equation.latex} />; } catch { return <code>{e.equation.latex}</code>; } })()}
                    </div>
                  ) : '-'}
                </td>
                <td>
                  {e.parameters.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                      {e.parameters.map((p, pi) => (
                        <li key={pi}>
                          {type === 'baseline' && (
                            <span style={{ color: '#888', fontSize: 11, marginRight: 4 }}>
                              [{p.para_type != null ? String(p.para_type) : t('sectionB71.allTypes')}]
                            </span>
                          )}
                          <code>{p.symbol}</code>
                          {p.annual_value != null && (
                            <span> = {fmtNum(p.annual_value)}{' '}
                              {p.unit && (
                                (() => { try { return <InlineMath math={p.unit} />; } catch { return <span>{p.unit}</span>; } })()
                              )}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : '-'}
                </td>
                <td>
                  {e.calculation_result.value != null ? (
                    <span style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span>{fmtNum(e.calculation_result.value)}</span>
                      {e.calculation_result.unit && (
                        <span style={{ fontSize: 12 }}>
                          {(() => { try { return <InlineMath math={e.calculation_result.unit} />; } catch { return e.calculation_result.unit; } })()}
                        </span>
                      )}
                    </span>
                  ) : '-'}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500,
                    background: e.is_sampling ? '#e8f4ff' : '#f5f5f5',
                    color: e.is_sampling ? '#4A90E2' : '#999',
                  }}>
                    {e.is_sampling ? t('sectionB71.yes') : t('sectionB71.no')}
                  </span>
                </td>
                {canEdit && (
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => onEdit(e)}
                        style={{ padding: '4px 10px', background: '#fff', border: '1px solid #4A90E2', color: '#4A90E2', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                        {t('sectionB71.edit')}
                      </button>
                      <button onClick={() => onDelete(e.id)}
                        style={{ padding: '4px 10px', background: '#fff', border: '1px solid #e53935', color: '#e53935', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                        {t('sectionB71.delete')}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
  );
};

// ── 主頁面 ────────────────────────────────────────────────────────
const SectionB71Page: React.FC = () => {
  const { i18n, t } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);
  const isZh = i18n.language === 'zh-TW';

  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState<SectionB71Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 公式精靈狀態
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardType, setWizardType] = useState<FormulaType>('bau');
  const [editEntry, setEditEntry] = useState<FormulaEntry | undefined>(undefined);

  // B.7.1.4 表單
  const [isDownward, setIsDownward] = useState(false);
  const [downwardDesc, setDownwardDesc] = useState('');
  const [savingDownward, setSavingDownward] = useState(false);

  // Session
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionB71Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // 載入資料
  const loadData = useCallback(async (projectId: number) => {
    try {
      setLoading(true);
      setError(null);
      const res = await sectionB71Service.getSectionB71ByProject(projectId);
      setData(res);
      if (res) {
        setIsDownward(res.is_downward);
        setDownwardDesc(res.downward_descript || '');
      }
    } catch (err: any) {
      if (err.response?.status === 404 || !err.response) {
        setData(null);
      } else {
        setError(err.response?.data?.detail || '載入資料失敗');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedProjectId) { setData(null); return; }
    loadData(selectedProjectId);
  }, [selectedProjectId, loadData]);

  const canEdit = hasPermission('section_b7_1', 'update');

  // BAU 合計
  const bauTotal = (data?.bau_calculate ?? []).reduce((sum, e) => sum + (e.calculation_result.value ?? 0), 0);
  const baselineTotal = (data?.baseline_calculate ?? []).reduce((sum, e) => sum + (e.calculation_result.value ?? 0), 0);
  const diffValue = bauTotal - baselineTotal;
  const isBaseline = baselineTotal <= bauTotal;

  // B.7.1.3 確認
  const handleConfirmDiff = async () => {
    if (!selectedProjectId) return;
    try {
      setLoading(true);
      const res = await sectionB71Service.updateSectionB71(selectedProjectId, {
        diff_calculate: diffValue,
        is_baselined: isBaseline,
      });
      setData(res);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('sectionB71.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  // B.7.1.4 儲存
  const handleSaveDownward = async () => {
    if (!selectedProjectId) return;
    // 開啟下修時，檢查富文本是否有實際內容
    if (isDownward) {
      const text = downwardDesc.replace(/<[^>]+>/g, '').trim();
      if (!text) {
        toast.warning(t('sectionB71.downwardRequired'));
        return;
      }
    }
    try {
      setSavingDownward(true);
      const res = await sectionB71Service.updateSectionB71(selectedProjectId, {
        is_downward: isDownward,
        downward_descript: isDownward ? downwardDesc : undefined,
      });
      setData(res);
      toast.success(t('sectionB71.saveSuccess'));
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('sectionB71.saveFailed'));
    } finally {
      setSavingDownward(false);
    }
  };

  // 重置全部 B.7.1 資料
  const handleReset = async () => {
    if (!selectedProjectId) return;
    const ok = await toast.confirm({
      title: isZh ? '操作確認' : 'Confirmation',
      message: t('sectionB71.resetConfirm'),
      confirmText: isZh ? '重設' : 'Reset',
    });
    if (!ok) return;
    try {
      setLoading(true);
      await sectionB71Service.resetSectionB71(selectedProjectId);
      setData(null);
      setIsDownward(false);
      setDownwardDesc('');
      setWizardOpen(false);
      setEditEntry(undefined);
      setActiveTab(0);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('sectionB71.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 刪除公式
  const handleDelete = async (type: FormulaType, entryId: string) => {
    if (!selectedProjectId) return;
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: t('sectionB71.deleteConfirm'),
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    try {
      if (type === 'bau') await sectionB71Service.deleteBauFormula(selectedProjectId, entryId);
      else await sectionB71Service.deleteBaselineFormula(selectedProjectId, entryId);
      await loadData(selectedProjectId);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('sectionB71.deleteFailed'));
    }
  };

  // 精靈回調
  const handleWizardSaved = (res: SectionB71Detail) => {
    setData(res);
    setWizardOpen(false);
    setEditEntry(undefined);
  };

  const openWizard = (type: FormulaType, entry?: FormulaEntry) => {
    setWizardType(type);
    setEditEntry(entry);
    setWizardOpen(true);
    // 切換到對應 tab
    setActiveTab(type === 'bau' ? 0 : 1);
  };

  const tabs = [
    { label: t('sectionB71.tab1') },
    { label: t('sectionB71.tab2') },
    { label: t('sectionB71.tab3') },
    { label: t('sectionB71.tab4') },
  ];

  if (permissionLoading) return <div className="page-container"><div className="loading">Loading...</div></div>;
  if (!hasPermission('section_b7_1', 'read')) return <div className="page-container"><div className="error-message">{t('sectionB71.noPermission')}</div></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_b7_1" />
        {canEdit && selectedProjectId && data && (
          <div className="page-header-right">
            <button
              onClick={handleReset}
              style={{
                padding: '8px 16px', borderRadius: 6, border: '1px solid #e53935',
                background: '#fff', color: '#e53935', cursor: 'pointer',
                fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
              }}
              title={t('sectionB71.resetTitle')}
            >
              🗑 {t('sectionB71.resetData')}
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProjectId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: 16 }}>
          {t('sectionB71.selectProject')}
        </div>
      ) : loading ? (
        <div className="loading">{t('sectionB71.loading')}</div>
      ) : (
        <div>
          {/* Tab Bar */}
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: 20, flexWrap: 'wrap' }}>
            {tabs.map((tab, i) => (
              <button key={i} onClick={() => { setActiveTab(i); setWizardOpen(false); setEditEntry(undefined); }}
                style={{
                  padding: '10px 18px', border: 'none', cursor: 'pointer', fontSize: 13,
                  borderBottom: activeTab === i ? '3px solid #4A90E2' : '3px solid transparent',
                  background: activeTab === i ? '#f0f7ff' : 'transparent',
                  color: activeTab === i ? '#4A90E2' : '#666',
                  fontWeight: activeTab === i ? 600 : 400,
                  marginBottom: -2,
                }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* B.7.1.1 日常計算 */}
          {activeTab === 0 && (
            <div>
              {wizardOpen && wizardType === 'bau' ? (
                <FormulaWizard
                  isZh={isZh} canEdit={canEdit} type="bau" projectId={selectedProjectId!}
                  editEntry={editEntry}
                  onSaved={handleWizardSaved}
                  onCancel={() => { setWizardOpen(false); setEditEntry(undefined); }}
                />
              ) : (
                <FormulaTable
                  isZh={isZh} canEdit={canEdit} type="bau"
                  entries={data?.bau_calculate ?? []}
                  onAdd={() => openWizard('bau')}
                  onEdit={e => openWizard('bau', e)}
                  onDelete={id => handleDelete('bau', id)}
                />
              )}
            </div>
          )}

          {/* B.7.1.2 基準計算 */}
          {activeTab === 1 && (
            <div>
              {wizardOpen && wizardType === 'baseline' ? (
                <FormulaWizard
                  isZh={isZh} canEdit={canEdit} type="baseline" projectId={selectedProjectId!}
                  editEntry={editEntry}
                  onSaved={handleWizardSaved}
                  onCancel={() => { setWizardOpen(false); setEditEntry(undefined); }}
                />
              ) : (
                <FormulaTable
                  isZh={isZh} canEdit={canEdit} type="baseline"
                  entries={data?.baseline_calculate ?? []}
                  onAdd={() => openWizard('baseline')}
                  onEdit={e => openWizard('baseline', e)}
                  onDelete={id => handleDelete('baseline', id)}
                />
              )}
            </div>
          )}

          {/* B.7.1.3 差額計算 */}
          {activeTab === 2 && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
                {[
                  { label: t('sectionB71.bauTotal'), value: bauTotal },
                  { label: t('sectionB71.baselineTotal'), value: baselineTotal },
                  { label: t('sectionB71.difference'), value: diffValue },
                ].map((item, i) => (
                  <div key={i} style={{ padding: 16, background: '#f8f8f8', borderRadius: 8, border: '1px solid #e0e0e0', textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: i === 2 ? (isBaseline ? '#52c41a' : '#e53935') : '#333' }}>
                      {fmtNum(item.value, 2)}
                    </div>
                    <div style={{ fontSize: 11, color: '#aaa' }}>tCO2e</div>
                  </div>
                ))}
              </div>

              {/* 符合標籤 */}
              <div style={{
                padding: '12px 20px', borderRadius: 8, marginBottom: 20,
                background: isBaseline ? '#f6ffed' : '#fff2f0',
                border: `1px solid ${isBaseline ? '#b7eb8f' : '#ffccc7'}`,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{ fontSize: 20 }}>{isBaseline ? '✅' : '❌'}</span>
                <span style={{ fontWeight: 600, color: isBaseline ? '#52c41a' : '#e53935' }}>
                  {isBaseline ? t('sectionB71.compliant') : t('sectionB71.nonCompliant')}
                </span>
              </div>

              {canEdit && isBaseline && (
                <div style={{ textAlign: 'center' }}>
                  <button onClick={handleConfirmDiff} style={{
                    padding: '10px 28px', background: '#52c41a', color: '#fff',
                    border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 15
                  }}>
                    {t('sectionB71.confirm')}
                  </button>
                </div>
              )}

              {data?.is_baselined && (
                <div style={{ marginTop: 12, color: '#52c41a', textAlign: 'center', fontWeight: 500 }}>
                  ✓ {t('sectionB71.confirmedSaved')}
                </div>
              )}
            </div>
          )}

          {/* B.7.1.4 下修 */}
          {activeTab === 3 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <label style={{ fontWeight: 600 }}>
                  {t('sectionB71.requiresDownward')}
                </label>
                <div
                  onClick={() => canEdit && setIsDownward(v => !v)}
                  style={{
                    width: 52, height: 28, borderRadius: 14, position: 'relative',
                    background: isDownward ? '#4A90E2' : '#ccc',
                    cursor: canEdit ? 'pointer' : 'not-allowed',
                    transition: 'background 0.25s',
                    flexShrink: 0,
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 3, left: isDownward ? 27 : 3,
                    width: 22, height: 22, borderRadius: '50%', background: '#fff',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                    transition: 'left 0.25s',
                  }} />
                </div>
                <span style={{ fontWeight: 500, color: isDownward ? '#4A90E2' : '#999' }}>
                  {isDownward ? t('sectionB71.yes') : t('sectionB71.no')}
                </span>
              </div>

              {isDownward && (
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                    {t('sectionB71.downwardDescLabel')}
                  </label>
                  <RichTextEditor
                    value={downwardDesc}
                    onChange={setDownwardDesc}
                    placeholder={t('sectionB71.downwardDescPlaceholder')}
                    disabled={!canEdit}
                  />
                </div>
              )}

              {canEdit && (
                <div style={{ textAlign: 'center' }}>
                  <button onClick={handleSaveDownward} disabled={savingDownward} style={{
                    padding: '10px 28px', background: savingDownward ? '#ccc' : '#4A90E2',
                    color: '#fff', border: 'none', borderRadius: 6,
                    cursor: savingDownward ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 15
                  }}>
                    {savingDownward ? t('sectionB71.saving') : t('sectionB71.save')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SectionB71Page;
